import { createClient } from '@supabase/supabase-js'
import emitter from '@/utils/emitter';

// הרחבת הטיפוס של SupabaseClient כדי שיכיר את הפונקציות שלנו
declare module '@supabase/supabase-js' {
  interface SupabaseClient {
    getUrl(): string;
    getKey(): string;
  }
}

// טיפוסי Supabase
type SupabaseClientType = ReturnType<typeof createClient>;

// קבועים של קונפיגורציה
const CONFIG = {
  // מקסימום זמן לחכות לתשובה מ-Supabase (במילישניות)
  REQUEST_TIMEOUT_MS: 15000,
  // זמן להמתין לאחר חזרה מטאב (ms)
  TAB_SWITCH_DELAY: 0,
};

// Handle both webpack and Vite environments
const getEnv = (key: string, defaultValue = '') => {
  // For webpack (Vue CLI) environment
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || defaultValue
  }
  
  // For Vite environment
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    // בדוק אם המשתנה קיים לפני שניגשים אליו
    if (Object.prototype.hasOwnProperty.call(import.meta.env, key)) {
      return import.meta.env[key] || defaultValue
    }
  }
  
  return defaultValue
}

// Development fallback values - replace with your actual keys for development
const SUPABASE_URL = 'https://qqfzjxjxdmyjyctyguci.supabase.co'
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFxZnpqeGp4ZG15anljdHlndWNpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ3MTU4NDIsImV4cCI6MjA2MDI5MTg0Mn0.7zi_SlICYApZ_lo_NOyOQxKjqlXy6pJH1TyLefrbfqo'

// נסה להשתמש במשתני סביבה של Vue CLI (webpack)
const supabaseUrl = getEnv('VUE_APP_SUPABASE_URL', SUPABASE_URL)
const supabaseAnonKey = getEnv('VUE_APP_SUPABASE_ANON_KEY', SUPABASE_ANON_KEY)

// Ensure we have a key before creating the client
if (!supabaseAnonKey) {
  console.warn('Supabase anonymous key is missing! Using development fallback key.')
}

// יצירת מופע יחיד של supabase
let supabaseInstance: SupabaseClientType | null = null;

/**
 * פונקציה ליצירת Supabase client חדש 
 */
export const createSupabaseClient = (): SupabaseClientType => {
  console.log('[Supabase] Creating new client instance');
  
  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true, // שמירת הסשן בין רענונים
      autoRefreshToken: true, // חידוש אוטומטי של הטוקן
      detectSessionInUrl: true // זיהוי סשן בכתובת URL
    }
  });
}

/**
 * הרחבת ה-Supabase Client עם פונקציות נוספות
 */
const extendSupabaseClient = (client: SupabaseClientType): SupabaseClientType => {
  // הוספת פונקציית הרחבה להשגת ה-URL
  client.getUrl = () => {
    return supabaseUrl;
  }

  // הוספת פונקציית הרחבה להשגת המפתח
  client.getKey = () => {
    return supabaseAnonKey;
  }
  
  return client;
}

/**
 * אתחול מחדש של ה-Supabase client
 * שימושי כאשר חוזרים מכרטיסיה או ממצב רקע
 */
export const reinitSupabaseClient = (): SupabaseClientType => {
  console.log('[Supabase] Reinitializing client to fix tab switching issues');
  
  // יצירת מופע חדש
  const newInstance = createSupabaseClient();
  
  // הוספת הפונקציות המורחבות
  extendSupabaseClient(newInstance);
  
  // עדכון המופע הגלובלי
  supabaseInstance = newInstance;
  
  console.log('[Supabase] Client reinitialized successfully');
  
  return newInstance;
}

/**
 * מחזיר את ה-Supabase client, ויוצר חדש אם צריך
 * תמיד מחזיר אובייקט תקין (never null) למניעת שגיאות TypeScript
 */
export const getSupabase = (): SupabaseClientType => {
  if (!supabaseInstance) {
    supabaseInstance = createSupabaseClient();
    extendSupabaseClient(supabaseInstance);
    
    // האזנה לאירועי חזרה מרקע
    if (typeof window !== 'undefined') {
      emitter.on('app:active', () => {
        // הפעלת אתחול מחדש של ה-client בתוך setTimeout
        // למניעת deadlock במקרה של onAuthStateChange
        setTimeout(() => {
          reinitSupabaseClient();
        }, CONFIG.TAB_SWITCH_DELAY);
      });
    }
  }
  
  return supabaseInstance;
}

// שמירת תאימות עם קוד קיים - כעת מובטח שלא יהיה null
export const supabase: SupabaseClientType = getSupabase();

/**
 * מעטפת לקריאות Supabase עם timeout
 * מאפשרת להגדיר זמן מקסימלי לחכות לתשובה
 */
export const executeWithTimeout = async <T>(
  operation: () => Promise<T>,
  timeoutMs = CONFIG.REQUEST_TIMEOUT_MS
): Promise<T> => {
  // יצירת promise שיתפוגג אחרי timeout
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Supabase operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  // התחרות בין ה-operation וה-timeout
  return Promise.race([operation(), timeoutPromise]);
};

/**
 * פונקציה בטוחה להרצת קריאות Supabase
 * משתמשת ב-setTimeout ו-timeout למניעת תקיעות
 */
export const safeSupabaseOperation = async <T>(
  operation: () => Promise<T>
): Promise<T> => {
  return new Promise((resolve, reject) => {
    // שימוש ב-setTimeout למניעת deadlock
    setTimeout(async () => {
      try {
        // שימוש במנגנון timeout למניעת תקיעות אינסופיות
        const result = await executeWithTimeout(operation);
        resolve(result);
      } catch (error) {
        // במקרה של שגיאה, ננסה לאתחל מחדש את Client ולנסות שוב
        try {
          reinitSupabaseClient();
          const result = await executeWithTimeout(operation);
          resolve(result);
        } catch (retryError) {
          reject(retryError);
        }
      }
    }, 0);
  });
};

/**
 * מעטפת בטוחה לkריאת onAuthStateChange
 * מונעת את בעיית deadlock כשחוזרים מטאב אחר
 */
export const safeAuthStateChange = (
  callback: (event: string, session: any) => void
) => {
  // קבלת ה-client העדכני ביותר
  const client = getSupabase();
  
  // הרצת onAuthStateChange
  const { data } = client.auth.onAuthStateChange((event, session) => {
    // עטיפת הקריאה בתוך setTimeout למניעת deadlock
    setTimeout(() => {
      callback(event, session);
    }, 0);
  });
  
  return data.subscription;
};

// ייצוא אובייקט API מאוחד שמכיל את כל הפונקציות הבטוחות
export const safeSupabase = {
  client: supabase,
  execute: safeSupabaseOperation,
  onAuthStateChange: safeAuthStateChange,
  reinitClient: reinitSupabaseClient
};